import { useEffect, useState } from "react";
import { globalStore } from "../utils/globalStore";
import type { Menu } from "../service/menu/types";
import type { PermissionKey } from "../constants/permissionKeys";

/** 递归检查菜单树的 meta.perms 是否等于目标权限标识 */
function hasMenuPermission(menus: Menu.Menu[], permission: string): boolean {
  for (const menu of menus) {
    if (menu.meta?.perms === permission) return true;
    if (menu.children && menu.children.length > 0 && hasMenuPermission(menu.children, permission)) {
      return true;
    }
  }
  return false;
}

/**
 * 功能可见性权限判定（三态）：
 * - `null`：用户信息 / 权限数据均未就绪 → 应隐藏（避免刷新 / 首登闪烁）
 * - `true`：持有该权限（userInfo.permissions 或菜单 meta.perms 任一命中）
 * - `false`：不持有
 *
 * 两个来源统一判定：后端既可以把权限放进 `userInfo.permissions`，
 * 也可以在菜单（meta.perms / 菜单权限标识）里配置，任一命中即显示。
 */
export function useHasPermission(key: PermissionKey): boolean | null {
  const [userInfo, setUserInfo] = useState(() => globalStore.getUserInfo());
  const [menuList, setMenuList] = useState(() => globalStore.getMenuList());

  useEffect(() => {
    const unsub = globalStore.subscribe(() => {
      setUserInfo(globalStore.getUserInfo());
      setMenuList(globalStore.getMenuList());
    });
    return unsub;
  }, []);

  if (!userInfo) return null;

  const permissions = userInfo.permissions;
  const permissionsReady = Array.isArray(permissions);
  const menuReady = menuList.length > 0;

  // 两个来源都尚未就绪 → 加载中（隐藏，避免闪烁）
  if (!permissionsReady && !menuReady) return null;

  if (permissionsReady && permissions.includes(key)) return true;
  if (menuReady && hasMenuPermission(menuList, key)) return true;

  return false;
}
